import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import moment from 'moment';
import withStyles from '@material-ui/styles/withStyles';
import ActivityLogsTable from 'features/wells/overview/activityLog/ActivityLogsTable';
import ActivityLogsGanttChart from 'features/wells/overview/activityLog/ActivityLogsGanttChart';
import ActivityLogFilter from 'features/wells/overview/activityLog/ActivityLogFilter';
import ActivityLogDrawer from 'features/wells/overview/activityLog/drawer/ActivityLogDrawer';
import {
  ACTIVITY_LOG_FORM_FEILDS,
  ACTIVITY_VIEWS,
  Filters,
  InitialFormValues,
  stringToEditorState,
} from 'features/wells/overview/activityLog/helpers/activityLog.constants';
import {
  deleteActivityLog,
  filterActivityLogs,
  getIntegrityManagementUsersByOrganization,
  upsertActivityLog,
} from 'features/wells/overview/activityLog/helpers/activityLog.actions';
import {
  getActivityLogsFromState,
  getIntegrityManagementUsersFromState,
} from 'features/wells/overview/activityLog/helpers/activityLog.selector';
import { Grid, Button } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { BasePage } from 'altus-ui-components';
import { useToggle } from 'altus-hooks';
import { connect } from 'react-redux';
import { getSummarizedDataStateFromState } from 'app/app.selectors';
import { useCallback } from 'react';
import { LoadingOverlay } from 'altus-ui-components';
import { isMomentObject } from 'features/wells/overview/activityLog/helpers/activityLog.validators';
import { EditorState } from 'draft-js';

const ActivityLogContainer = ({
  classes,
  wellboreId,
  dataState,
  users,
  activityLogs,
  disptachFilterActivityLogs,
  dispatchGetIntegrityManagementUsersByOrganization,
  disptachUpsertActivityLog,
  dispatchDeleteActivityLog,
  selectedView,
}) => {
  const [showActivityDrawer, toggleActivityDrawer] = useToggle();
  const [initialActivityLogFormValues, setInitialActivityLogFormValues] =
    useState({
      [ACTIVITY_LOG_FORM_FEILDS.ACTIVITY_LOG_ID]: null,
      [ACTIVITY_LOG_FORM_FEILDS.ACTIVITY_NAME]: '',
      [ACTIVITY_LOG_FORM_FEILDS.DESCRIPTION]: '',
      [ACTIVITY_LOG_FORM_FEILDS.START_DATE]: '',
      [ACTIVITY_LOG_FORM_FEILDS.END_DATE]: '',
      [ACTIVITY_LOG_FORM_FEILDS.STATUS]: '',
      [ACTIVITY_LOG_FORM_FEILDS.RESPONSIBLE]: '',
      [ACTIVITY_LOG_FORM_FEILDS.COMMENT]: '',
      [ACTIVITY_LOG_FORM_FEILDS.MITIGATION_SOLUTIONS]: '',
    });

  const [isEdit, setIsEdit] = useState();
  const [activityLogsFromState, setActivityLogsFromState] = useState();
  const [filterFormValues, setFilterFormValues] = useState(InitialFormValues);

  useEffect(() => {
    setActivityLogsFromState(activityLogs);
  }, [activityLogs]);

  const resetActivityFormValues = useCallback(() => {
    setInitialActivityLogFormValues({
      [ACTIVITY_LOG_FORM_FEILDS.ACTIVITY_LOG_ID]: null,
      [ACTIVITY_LOG_FORM_FEILDS.ACTIVITY_NAME]: '',
      [ACTIVITY_LOG_FORM_FEILDS.DESCRIPTION]: '',
      [ACTIVITY_LOG_FORM_FEILDS.START_DATE]: '',
      [ACTIVITY_LOG_FORM_FEILDS.END_DATE]: '',
      [ACTIVITY_LOG_FORM_FEILDS.STATUS]: '',
      [ACTIVITY_LOG_FORM_FEILDS.RESPONSIBLE]: '',
      [ACTIVITY_LOG_FORM_FEILDS.COMMENT]: '',
      [ACTIVITY_LOG_FORM_FEILDS.MITIGATION_SOLUTIONS]: '',
    });
  }, []);

  const upsertActivityLog = useCallback(
    (formValues) => {
      disptachUpsertActivityLog(wellboreId, formValues, toggleActivityDrawer);
      resetActivityFormValues();
    },
    [
      disptachUpsertActivityLog,
      wellboreId,
      toggleActivityDrawer,
      resetActivityFormValues,
    ],
  );

  const deleteActivityLog = useCallback(
    (activityLogId) => {
      dispatchDeleteActivityLog(activityLogId, wellboreId);
    },
    [dispatchDeleteActivityLog, wellboreId],
  );

  useEffect(() => {
    dispatchGetIntegrityManagementUsersByOrganization();
    disptachFilterActivityLogs(wellboreId, InitialFormValues);
  }, [
    dispatchGetIntegrityManagementUsersByOrganization,
    disptachFilterActivityLogs,
    wellboreId,
  ]);

  const onFiltersChange = useCallback(
    (formValues) => {
      const filters = {
        [Filters.NAME]: formValues.name,
        [Filters.STATUS]:
          formValues.status instanceof Number ||
          typeof formValues.status === 'number'
            ? [formValues.status]
            : [],
        [Filters.FROM]: isMomentObject(formValues.from)
          ? formValues.from.format()
          : moment(formValues.from).format(),
        [Filters.TO]: isMomentObject(formValues.to)
          ? formValues.to.format()
          : moment(formValues.to).format(),
      };
      setFilterFormValues(filters);
      disptachFilterActivityLogs(wellboreId, filters);
    },
    [disptachFilterActivityLogs, wellboreId],
  );

  useEffect(() => {
    if (isEdit) {
      setInitialActivityLogFormValues({
        [ACTIVITY_LOG_FORM_FEILDS.ACTIVITY_LOG_ID]: isEdit.activityLogId,
        [ACTIVITY_LOG_FORM_FEILDS.ACTIVITY_NAME]: isEdit.activityName,
        [ACTIVITY_LOG_FORM_FEILDS.DESCRIPTION]: isEdit.description
          ? stringToEditorState(isEdit.description)
          : EditorState.createEmpty(),
        [ACTIVITY_LOG_FORM_FEILDS.START_DATE]: isEdit.startDate,
        [ACTIVITY_LOG_FORM_FEILDS.END_DATE]: isEdit.endDate,
        [ACTIVITY_LOG_FORM_FEILDS.STATUS]: isEdit.status,
        [ACTIVITY_LOG_FORM_FEILDS.RESPONSIBLE]:
          isEdit.responsibleUser.directoryId,
        [ACTIVITY_LOG_FORM_FEILDS.COMMENT]: isEdit.comment
          ? stringToEditorState(isEdit.comment)
          : EditorState.createEmpty(),
        [ACTIVITY_LOG_FORM_FEILDS.MITIGATION_SOLUTIONS]:
          isEdit.mitigationSolutions
            ? stringToEditorState(isEdit.mitigationSolutions)
            : EditorState.createEmpty(),
      });
      toggleActivityDrawer();
      setIsEdit(null);
    }
  }, [isEdit, toggleActivityDrawer]);

  return (
    <BasePage dataState={dataState}>
      <Grid item container paddingTop={2} paddingLeft={2} paddingRight={2}>
        <Grid xs={7} item>
          <ActivityLogFilter
            onSubmit={onFiltersChange}
            initialValues={InitialFormValues}
          />
        </Grid>
        <Grid xs={3} />
        <Grid xs={2} item className={classes.buttonContainer}>
          {showActivityDrawer && (
            <ActivityLogDrawer
              isOpen={showActivityDrawer}
              toggleDrawer={toggleActivityDrawer}
              responsibleUsers={users}
              initialActivityLogFormValues={initialActivityLogFormValues}
              upsertActivityLog={upsertActivityLog}
              isEdit={isEdit}
              resetActivityFormValues={resetActivityFormValues}
            />
          )}
          <Button
            title="New Activity"
            color="primary"
            variant="contained"
            onClick={() => toggleActivityDrawer()}
          >
            <AddIcon fontSize="small" />
            New Activity
          </Button>
        </Grid>
        <Grid xs={12} item className={classes.activityTable}>
          {selectedView === ACTIVITY_VIEWS.CHART ? (
            <ActivityLogsGanttChart
              activityLogs={activityLogsFromState}
              filter={filterFormValues}
            />
          ) : (
            <ActivityLogsTable
              activityLogs={activityLogsFromState}
              setIsEdit={setIsEdit}
              deleteActivityLog={deleteActivityLog}
            />
          )}
        </Grid>
      </Grid>
      <LoadingOverlay dataState={dataState} />
    </BasePage>
  );
};

const styles = (theme) => ({
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingTop: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  activityTable: {
    scrollY: 'auto',
  },
});

export default compose(
  connect(
    (state) => ({
      users: getIntegrityManagementUsersFromState(state),
      activityLogs: getActivityLogsFromState(state),
      dataState: getSummarizedDataStateFromState(state),
    }),
    {
      dispatchGetIntegrityManagementUsersByOrganization:
        getIntegrityManagementUsersByOrganization,
      disptachUpsertActivityLog: upsertActivityLog,
      disptachFilterActivityLogs: filterActivityLogs,
      dispatchDeleteActivityLog: deleteActivityLog,
    },
  ),
  withStyles(styles),
)(ActivityLogContainer);
